import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ProjectInquiryForm from './components/ProjectInquiryForm';
import ContactInfo from './components/ContactInfo';
import AvailabilityCalendar from './components/AvailabilityCalendar';

const ContactForm = () => {
  return (
    <>
      <Helmet>
        <title>Contact - DevPortfolio Pro | Get In Touch</title>
        <meta 
          name="description" 
          content="Get in touch with DevPortfolio Pro for your next project. Schedule a consultation, send a message, or connect through social media." 
        />
        <meta name="keywords" content="contact, hire developer, project inquiry, consultation, full-stack developer" />
        <meta property="og:title" content="Contact - DevPortfolio Pro" />
        <meta property="og:description" content="Ready to start your next project? Let's discuss how I can help bring your ideas to life." />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Let's Build Something
              <span className="text-primary block">Amazing Together</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Ready to turn your ideas into reality? I'm here to help you create exceptional digital experiences that drive results.
            </p>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary mb-1">24h</div>
                <div className="text-sm text-muted-foreground">Response Time</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary mb-1">50+</div>
                <div className="text-sm text-muted-foreground">Projects Completed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary mb-1">5★</div>
                <div className="text-sm text-muted-foreground">Client Rating</div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="pb-16 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Form */}
              <div className="lg:col-span-2">
                <ProjectInquiryForm />
              </div>

              {/* Right Column - Contact Info & Calendar */}
              <div className="space-y-8">
                <ContactInfo />
                <AvailabilityCalendar />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-6 lg:px-8 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    What's your typical response time?
                  </h3>
                  <p className="text-muted-foreground">
                    I respond to all inquiries within 24 hours during business days. For urgent projects, I'm often available for same-day responses.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Do you work with international clients?
                  </h3>
                  <p className="text-muted-foreground">
                    Absolutely! I work with clients worldwide and am comfortable with different time zones and communication preferences.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    What information should I include in my inquiry?
                  </h3>
                  <p className="text-muted-foreground">
                    Please include your project goals, timeline, budget range, and any specific requirements. The more details you provide, the better I can assist you.
                  </p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Do you offer free consultations?
                  </h3>
                  <p className="text-muted-foreground">
                    Yes! I offer a free 30-minute consultation to discuss your project and determine if we're a good fit for working together.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    What's your project minimum?
                  </h3>
                  <p className="text-muted-foreground">
                    I typically work on projects starting from $5,000. However, I'm open to discussing smaller projects or ongoing maintenance work.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    How do you handle project communication?
                  </h3>
                  <p className="text-muted-foreground">
                    I provide regular updates through your preferred communication channel and use project management tools to keep everything organized and transparent.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-6 lg:px-8 border-t border-border">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-muted-foreground">
              © {new Date()?.getFullYear()} Kiddy-Dev. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ContactForm;